import React, { useContext, useState } from "react";

import { useCloudFileApi } from "../../../common/api/master-api/cloud-files/useCloudFileApi";
import { useMonitoringApi } from "../../../common/api/monitoring-api/useMonitoringApi";
import { ExecutionType } from "../../../common/types/ExecutionType";
import { UserContext } from "../../../core/security/UserProvider";
import { CloudFileStatus } from "../common/types/CloudFileStatus";
import { CloudFileStatusDetails } from "../common/types/CloudFileStatusDetails";
import { CloudFileListingContext } from "./CloudFileListingContext";
import { CloudFileListingFilters } from "./hooks/useCloudFileListingFilters";
import { mapToCloudFileListingItem, CloudFileListingItem } from "./types/CloudFileListingItem";

export type CloudFileListingProviderProps = React.PropsWithChildren<unknown>;

export const CloudFileListingProvider: React.FC<CloudFileListingProviderProps> = (
    props: CloudFileListingProviderProps
) => {
    const { getExecutions } = useMonitoringApi();

    const { getCloudFileList } = useCloudFileApi();

    const [items, setItems] = useState<CloudFileListingItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<CloudFileListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const userContext = useContext(UserContext);

    const reloadData = async (filters: CloudFileListingFilters): Promise<void> => {
        const response = await getCloudFileList({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            fileName: filters.fileName,
            deliveryRef: filters.deliveryRef,
            arrivalDateStart: filters.arrivalDateStart?.toISOString(),
            arrivalDateEnd: filters.arrivalDateEnd?.toISOString(),
            statuses: filters.statuses
        });

        const cloudFileListingItems = response.items.map(mapToCloudFileListingItem);

        setItems(cloudFileListingItems);
        setTotalItems(response.totalCount);

        lazyLoadExecutions(cloudFileListingItems);
    };

    const lazyLoadExecutions = async (cloudFileListingItems: CloudFileListingItem[]) => {
        if (!cloudFileListingItems.length) {
            return;
        }

        const ids = cloudFileListingItems.map((item) => item.id);

        const response = await getExecutions(ExecutionType.CloudFile, ids);

        setItems((prevItems) =>
            prevItems.map((prevItem) => {
                const itemExecutionResponse = response.items.find((item) => item.entityId === prevItem.id);

                prevItem.executionArn = itemExecutionResponse?.executionArn;

                return prevItem;
            })
        );
    };

    const repeatItemProcessing = (cloudFileId: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === cloudFileId ? { ...item, isModified: true, status: CloudFileStatus.PrepForQc } : item
            )
        );
    };

    const approveNotifications = (itemId: string) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === itemId ? { ...item, isModified: true, isNotificationApproved: true } : item
            )
        );
    };

    const abandonItem = (cloudFileId: string): void => {
        setItems((prevItems) => prevItems.filter((prevItem) => prevItem.id !== cloudFileId));
    };

    const abandonItems = (cloudFileIds: string[]): void => {
        setItems((prevItems) => prevItems.filter((prevItem) => !cloudFileIds.includes(prevItem.id)));
    };

    const rejectCloudFile = (cloudFileId: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === cloudFileId ? { ...item, isModified: true, status: CloudFileStatus.Rejected } : item
            )
        );
    };

    const updateCloudFileStatus = (
        cloudFileId: string,
        status: CloudFileStatus,
        statusDetails?: CloudFileStatusDetails
    ) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === cloudFileId
                    ? {
                          ...item,
                          isModified: true,
                          status,
                          statusDetails: statusDetails ?? item.statusDetails,
                          lastUpdatedBy: userContext.name,
                          updateDate: new Date().toISOString()
                      }
                    : item
            )
        );
    };

    return (
        <CloudFileListingContext.Provider
            value={{
                items,
                selectedItems,
                totalItems,
                reloadData,
                setSelectedItems,
                repeatItemProcessing,
                approveNotifications,
                abandonItem,
                abandonItems,
                rejectCloudFile,
                updateCloudFileStatus
            }}
        >
            {props.children}
        </CloudFileListingContext.Provider>
    );
};
